/**
 * SchoolsPage ✦ iter193 — Leavers Night Consolidation
 * ─────────────────────────────────────────────────────────────
 * One authored experience. One price. One emotional shape.
 *
 * Constitutional citations (per /app/memory/EVENTDROP_BELIEF_SYSTEM.md):
 *   • Belief 4 — the evening becomes a system, not software.
 *     One package, not three, lets schools BE a shape — not a
 *     configurable catalogue.
 *   • Belief 5 — different evenings have different emotional
 *     centres. The schools emotional centre is the year group
 *     writing itself, regardless of Year 6 vs Year 11.
 *   • Belief 6 — luxury implies depth; cheap explains everything.
 *     A single package implies depth; three packages explained too
 *     much.
 *   • Negative space — NOT event-management SaaS · NOT a
 *     configurable product catalogue · NOT a feature ladder.
 *
 * Bundle decision (locked iter193, repriced iter215): Spin Film
 * 360 is RESERVED for The Archive ✦ £99. Including it in Leavers
 * Night would erode The Archive's narrative gravity — and the
 * year group's emotional centre is participation, not cinematic
 * preservation. Coherent pricing ladder protected.
 *
 * Architectural rules inherited from iter192:
 *   • No feature grids. Editorial pacing only.
 *   • No icons. Typography + spacing carry the meaning.
 *   • No upgrade pressure, no padlocks, no "most popular" badges.
 *   • Motion is environmental — opacity + soft drift, never theatrical.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { SchoolsFooter } from '../components/SchoolsEditorialChapter';

// ── Constitutional tokens (locked across homepage + schools) ─────
const SERIF = 'Georgia, "Cormorant Garamond", "Times New Roman", serif';
const IVORY = '#FBEED0';
const IVORY_DIM = 'rgba(251,238,208,0.72)';
const IVORY_GHOST = 'rgba(251,238,208,0.48)';
const GOLD = '#D4AF5E';
const GOLD_MUTED = 'rgba(212,170,82,0.78)';
const GOLD_FAINT = 'rgba(212,170,82,0.18)';
const WALNUT = '#0E0805';
const WALNUT_DEEP = '#080503';

const track = (event) => {
  try {
    const url = `${process.env.REACT_APP_BACKEND_URL || ''}/api/demo/track`;
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event }),
      keepalive: true,
    }).catch(() => {});
  } catch { /* ignore */ }
};

const calmRise = {
  hidden: { opacity: 0, y: 14 },
  show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.22, 0.61, 0.36, 1] } },
};
const calmStagger = (delay = 0, gap = 0.16) => ({
  hidden: {},
  show: { transition: { delayChildren: delay, staggerChildren: gap } },
});

// =========================================================================
// PAGE
// =========================================================================
export default function SchoolsPage() {
  return (
    <div
      data-testid="schools-page"
      style={{ background: WALNUT, color: IVORY, fontFamily: SERIF, minHeight: '100vh', overflowX: 'hidden' }}
    >
      <SchoolsNav />
      <Hero />
      <WhatTheRoomBecomes />
      <YearGroupAcknowledgement />
      <LeaversNightPackage />
      <WhatRemains />
      <Closing />
      <SchoolsFooter />
      <GlobalStyles />
    </div>
  );
}

// =========================================================================
// NAV — dedicated schools header (per brief)
// =========================================================================
const SchoolsNav = () => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  const quietLink = {
    fontFamily: SERIF, fontStyle: 'italic',
    fontSize: 14, color: IVORY_DIM,
    textDecoration: 'underline',
    textDecorationColor: GOLD_FAINT,
    textUnderlineOffset: 4,
  };
  return (
    <header
      data-testid="schools-nav"
      style={{
        position: 'sticky', top: 0, zIndex: 50,
        background: scrolled ? 'rgba(16,10,6,0.66)' : 'transparent',
        backdropFilter: scrolled ? 'blur(14px)' : 'blur(0px)',
        borderBottom: scrolled ? `1px solid ${GOLD_FAINT}` : '1px solid transparent',
        padding: '14px clamp(14px, 4vw, 24px)',
        transition: 'background 480ms ease, backdrop-filter 480ms ease, border-color 480ms ease',
      }}
    >
      <div style={{
        maxWidth: 1180, margin: '0 auto',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 22,
      }}>
        <Link to="/" data-testid="schools-nav-home" style={{
          fontFamily: SERIF, fontSize: 'clamp(20px, 5vw, 22px)', color: IVORY,
          textDecoration: 'none', letterSpacing: '-0.005em', fontWeight: 500,
        }}>
          EventDrop <span style={{ color: GOLD, fontSize: 14, marginLeft: 2 }}>✦</span>
        </Link>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'clamp(10px, 2vw, 22px)' }}>
          <span style={{
            fontFamily: SERIF, fontStyle: 'italic',
            fontSize: 13, letterSpacing: '0.18em', textTransform: 'uppercase',
            color: GOLD_MUTED,
          }} data-testid="schools-nav-eyebrow">
            For schools ✦
          </span>
          <Link to="/login" style={quietLink} data-testid="schools-nav-signin">Sign in</Link>
        </div>
      </div>
    </header>
  );
};

// =========================================================================
// SECTION 1 — HERO
// Leavers Night ✦ — A live participation experience for the year group.
// =========================================================================
const Hero = () => {
  const reduce = useReducedMotion();
  return (
    <section data-testid="schools-hero" style={{ position: 'relative', overflow: 'hidden' }}>
      <HeroAtmosphere reduce={reduce} />
      <motion.div
        initial="hidden"
        animate="show"
        variants={calmStagger(0.2, 0.22)}
        style={{
          position: 'relative', zIndex: 1,
          maxWidth: 980, margin: '0 auto',
          padding: 'clamp(96px, 14vh, 168px) 24px clamp(80px, 11vh, 120px)',
          textAlign: 'center',
        }}
      >
        <motion.p variants={calmRise} style={{
          margin: 0, marginBottom: 24,
          fontFamily: SERIF, fontStyle: 'italic',
          fontSize: 11, letterSpacing: '0.32em', textTransform: 'uppercase',
          color: GOLD_MUTED,
        }} data-testid="schools-hero-eyebrow">
          For schools ✦
        </motion.p>
        <motion.h1 variants={calmRise} style={{
          margin: 0,
          fontFamily: SERIF, fontWeight: 500,
          fontSize: 'clamp(44px, 8vw, 84px)',
          lineHeight: 1.02, letterSpacing: '-0.02em',
          color: IVORY,
          textShadow: '0 0 32px rgba(212,170,82,0.10)',
        }} data-testid="schools-hero-headline">
          Leavers Night <span style={{ color: GOLD, fontStyle: 'normal' }}>✦</span>
        </motion.h1>
        <motion.p variants={calmRise} style={{
          margin: '28px auto 0', maxWidth: 620,
          fontFamily: SERIF, fontStyle: 'italic',
          fontSize: 'clamp(17px, 2vw, 21px)', lineHeight: 1.55,
          color: IVORY,
        }} data-testid="schools-hero-subhead">
          A live participation experience for the year group <span style={{ color: GOLD, fontStyle: 'normal' }}>✦</span>
        </motion.p>
        <motion.p variants={calmRise} style={{
          margin: '32px auto 0', maxWidth: 580,
          fontFamily: SERIF, fontStyle: 'italic',
          fontSize: 'clamp(15px, 1.6vw, 17px)', lineHeight: 1.75,
          color: IVORY_DIM,
        }} data-testid="schools-hero-body">
          One QR quietly opens the evening.
          <br />
          Photos, messages, live walls, handwritten moments, projector
          experiences and shared memories — all forming together as the
          night unfolds.
        </motion.p>
        <motion.div variants={calmRise} style={{ marginTop: 48 }}>
          <a
            href="#book-leavers-night"
            data-testid="schools-hero-cta"
            onClick={() => track('schools_hero_book')}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 10,
              padding: '16px 32px', borderRadius: 999,
              background: 'rgba(18,12,6,0.55)',
              border: `1px solid ${GOLD_MUTED}`,
              backdropFilter: 'blur(10px)',
              color: IVORY,
              fontFamily: SERIF, fontStyle: 'italic',
              fontSize: 16, letterSpacing: '0.01em',
              textDecoration: 'none',
              transition: 'border-color 360ms ease, color 360ms ease',
            }}
          >
            Book Leavers Night <span style={{ color: GOLD }}>✦</span>
          </a>
        </motion.div>
      </motion.div>
    </section>
  );
};

const HeroAtmosphere = ({ reduce }) => (
  <>
    <div aria-hidden style={{
      position: 'absolute', inset: 0,
      background:
        `radial-gradient(ellipse 90% 70% at 50% 35%, rgba(28,18,10,0.0) 0%, rgba(14,8,5,0.55) 50%, rgba(8,5,3,0.95) 95%),` +
        `radial-gradient(ellipse 60% 50% at 50% 25%, rgba(212,170,82,0.06) 0%, transparent 70%)`,
      pointerEvents: 'none',
    }} />
    <motion.div
      aria-hidden
      animate={reduce ? { opacity: 0.5 } : { opacity: [0.35, 0.6, 0.4], y: [0, -10, 0] }}
      transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }}
      style={{
        position: 'absolute', left: '50%', top: '32%',
        transform: 'translateX(-50%)',
        width: 720, height: 320, maxWidth: '90vw',
        background: `radial-gradient(ellipse at center, rgba(212,170,82,0.10) 0%, transparent 65%)`,
        filter: 'blur(20px)',
        pointerEvents: 'none',
      }}
    />
  </>
);

// =========================================================================
// SECTION 2 — WHAT THE ROOM BECOMES
// Ways the year group leaves itself behind — NOT features.
// =========================================================================
const ROOM_MOMENTS = [
  {
    id: 'pocket-booth',
    name: 'Pocket Booth',
    line: 'Every phone, briefly, a portrait booth — the year group passing it around.',
  },
  {
    id: 'memory-snap',
    name: 'Memory Snap',
    line: 'Photos arriving from the room into the same place, as the night happens.',
  },
  {
    id: 'lens-notes',
    name: 'Lens Notes',
    line: 'Handwritten messages, photographed and folded into the year group’s chapter.',
  },
  {
    id: 'remember-us',
    name: 'Remember Us ✦',
    line: 'A collaborative wall the year group draws onto, together, until the room feels inhabited.',
  },
  {
    id: 'drop-one-word',
    name: 'Drop One Word',
    line: 'A single word from each person — a portrait of the room in its own vocabulary.',
  },
  {
    id: 'projector-wall',
    name: 'Live Projector Wall',
    line: 'The room reacting to itself on the wall behind them as the evening unfolds.',
  },
];

const WhatTheRoomBecomes = () => (
  <section data-testid="schools-room-becomes" style={{
    background: WALNUT_DEEP,
    padding: 'clamp(96px, 14vh, 160px) 24px',
  }}>
    <div style={{ maxWidth: 920, margin: '0 auto' }}>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-12%' }}
        transition={{ duration: 1.0, ease: [0.22, 0.61, 0.36, 1] }}
        style={{ textAlign: 'center', marginBottom: 'clamp(64px, 9vh, 96px)' }}
      >
        <p style={{
          margin: 0, marginBottom: 14,
          fontFamily: SERIF, fontStyle: 'italic',
          fontSize: 11, letterSpacing: '0.28em', textTransform: 'uppercase',
          color: GOLD_MUTED,
        }}>What the room becomes ✦</p>
        <h2 style={{
          margin: '0 auto', maxWidth: 700,
          fontFamily: SERIF, fontWeight: 500,
          fontSize: 'clamp(28px, 4vw, 40px)',
          lineHeight: 1.16, letterSpacing: '-0.01em',
          color: IVORY,
        }}>
          Six ways the year group leaves itself behind.
        </h2>
      </motion.div>
      <motion.ul
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: '-10%' }}
        variants={calmStagger(0, 0.12)}
        style={{
          listStyle: 'none', margin: 0, padding: 0,
          display: 'grid', gap: 'clamp(32px, 5vh, 48px)',
        }}
      >
        {ROOM_MOMENTS.map((m, i) => (
          <motion.li
            key={m.id}
            variants={calmRise}
            data-testid={`room-moment-${m.id}`}
            style={{
              display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 24,
              alignItems: 'baseline',
              maxWidth: 820, margin: '0 auto',
              paddingBottom: i < ROOM_MOMENTS.length - 1 ? 32 : 0,
              borderBottom: i < ROOM_MOMENTS.length - 1 ? `1px solid rgba(212,170,82,0.08)` : 'none',
            }}
          >
            <span style={{
              fontFamily: SERIF, fontStyle: 'italic',
              fontSize: 13, color: GOLD_MUTED, letterSpacing: '0.18em',
              minWidth: 28,
            }}>
              {String(i + 1).padStart(2, '0')}
            </span>
            <div>
              <p style={{
                margin: 0,
                fontFamily: SERIF, fontWeight: 500,
                fontSize: 'clamp(20px, 2.4vw, 24px)', letterSpacing: '-0.005em',
                color: IVORY,
              }}>
                {m.name}
              </p>
              <p style={{
                margin: '8px 0 0', maxWidth: 640,
                fontFamily: SERIF, fontStyle: 'italic',
                fontSize: 'clamp(15px, 1.6vw, 17px)', lineHeight: 1.65,
                color: IVORY_DIM,
              }}>
                {m.line}
              </p>
            </div>
          </motion.li>
        ))}
      </motion.ul>
    </div>
  </section>
);

// =========================================================================
// SECTION 3 — YEAR GROUP ACKNOWLEDGEMENT
// Brief editorial. NO branching flows. NO separate products.
// =========================================================================
const YearGroupAcknowledgement = () => (
  <section data-testid="schools-year-group-ack" style={{
    background: WALNUT,
    padding: 'clamp(80px, 12vh, 128px) 24px',
  }}>
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-12%' }}
      transition={{ duration: 1.1, ease: [0.22, 0.61, 0.36, 1] }}
      style={{ maxWidth: 760, margin: '0 auto', textAlign: 'center' }}
    >
      <p style={{
        margin: 0, marginBottom: 28,
        fontFamily: SERIF, fontStyle: 'italic',
        fontSize: 'clamp(16px, 1.8vw, 19px)', lineHeight: 1.65,
        color: IVORY_DIM, maxWidth: 640, marginLeft: 'auto', marginRight: 'auto',
      }} data-testid="ack-prologue">
        The same system carries both ends of the year.
      </p>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: 'clamp(40px, 6vh, 64px)',
        marginTop: 40,
      }}>
        <div data-testid="ack-year6">
          <p style={{
            margin: 0, marginBottom: 12,
            fontFamily: SERIF, fontStyle: 'italic',
            fontSize: 11, letterSpacing: '0.28em', textTransform: 'uppercase',
            color: GOLD_MUTED,
          }}>For Year 6 ✦</p>
          <p style={{
            margin: 0,
            fontFamily: SERIF, fontStyle: 'italic',
            fontSize: 'clamp(20px, 2.4vw, 24px)', lineHeight: 1.35,
            color: IVORY, letterSpacing: '-0.005em',
          }}>
            The classroom remembering itself.
          </p>
        </div>
        <div data-testid="ack-year11">
          <p style={{
            margin: 0, marginBottom: 12,
            fontFamily: SERIF, fontStyle: 'italic',
            fontSize: 11, letterSpacing: '0.28em', textTransform: 'uppercase',
            color: GOLD_MUTED,
          }}>For Year 11 and Sixth Form ✦</p>
          <p style={{
            margin: 0,
            fontFamily: SERIF, fontStyle: 'italic',
            fontSize: 'clamp(20px, 2.4vw, 24px)', lineHeight: 1.35,
            color: IVORY, letterSpacing: '-0.005em',
          }}>
            The night the year group crossed.
          </p>
        </div>
      </div>
    </motion.div>
  </section>
);

// =========================================================================
// SECTION 4 — THE PACKAGE
// One package. One price. No comparison logic.
// =========================================================================
const LEAVERS_NIGHT_INCLUDES = [
  'Booth OS',
  'Remember Us ✦',
  'Pocket Booth',
  'Memory Snap',
  'Lens Notes',
  'Drop One Word',
  'Live projector wall',
  'Shared atmosphere system',
];

const LeaversNightPackage = () => (
  <section
    id="book-leavers-night"
    data-testid="schools-package"
    style={{
      background: WALNUT_DEEP,
      padding: 'clamp(96px, 14vh, 160px) 24px',
    }}
  >
    <motion.article
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-10%' }}
      transition={{ duration: 1.1, ease: [0.22, 0.61, 0.36, 1] }}
      style={{
        maxWidth: 720, margin: '0 auto',
        padding: 'clamp(40px, 5vw, 56px)',
        background: `linear-gradient(180deg, rgba(28,18,10,0.65) 0%, rgba(14,8,5,0.78) 100%)`,
        border: `1px solid ${GOLD_MUTED}`,
        borderRadius: 18,
        boxShadow: `0 30px 90px rgba(0,0,0,0.5), 0 0 80px rgba(212,170,82,0.06) inset`,
        backdropFilter: 'blur(8px)',
      }}
    >
      <header style={{ textAlign: 'center', marginBottom: 36 }}>
        <p style={{
          margin: 0, marginBottom: 12,
          fontFamily: SERIF, fontStyle: 'italic',
          fontSize: 12, letterSpacing: '0.24em', textTransform: 'uppercase',
          color: GOLD,
        }} data-testid="package-eyebrow">
          Leavers Night ✦
        </p>
        <h2 style={{
          margin: 0,
          fontFamily: SERIF, fontWeight: 500,
          fontSize: 'clamp(30px, 3.8vw, 40px)', lineHeight: 1.12, letterSpacing: '-0.01em',
          color: IVORY,
        }} data-testid="package-heading">
          One evening. One shared system.
        </h2>
        <p style={{
          margin: '20px auto 0', maxWidth: 460,
          fontFamily: SERIF, fontStyle: 'italic',
          fontSize: 15, lineHeight: 1.65,
          color: IVORY_DIM,
        }} data-testid="package-subhead">
          Live participation for the year group, end-to-end. No tiers. No upgrades.
        </p>
      </header>
      <ul style={{
        listStyle: 'none', margin: 0, padding: 0,
        display: 'grid', gap: 14,
        maxWidth: 460, marginLeft: 'auto', marginRight: 'auto',
      }} data-testid="package-includes">
        {LEAVERS_NIGHT_INCLUDES.map((line) => (
          <li key={line} style={{
            fontFamily: SERIF,
            fontSize: 16, letterSpacing: '0.005em',
            color: IVORY, textAlign: 'center',
          }}>
            {line}
          </li>
        ))}
      </ul>
      <p style={{
        margin: '40px auto 0', maxWidth: 460, textAlign: 'center',
        fontFamily: SERIF, fontStyle: 'italic',
        fontSize: 13, color: IVORY_GHOST, letterSpacing: '0.04em',
      }} data-testid="package-audience-note">
        Built for Year 6 leavers, prom nights and sixth form celebrations <span style={{ color: GOLD }}>✦</span>
      </p>
      <footer style={{
        marginTop: 48, paddingTop: 36,
        borderTop: `1px solid ${GOLD_FAINT}`,
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 24,
      }}>
        <p style={{
          margin: 0,
          fontFamily: SERIF, fontWeight: 500,
          fontSize: 'clamp(36px, 4.4vw, 48px)', letterSpacing: '-0.01em',
          color: GOLD,
        }} data-testid="package-price">
          £99
        </p>
        <Link
          to="/create?edition=leavers_night"
          data-testid="schools-package-cta"
          onClick={() => track('schools_package_book')}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 10,
            padding: '16px 32px', borderRadius: 999,
            background: 'rgba(212,170,82,0.10)',
            border: `1px solid ${GOLD}`,
            color: IVORY,
            fontFamily: SERIF, fontStyle: 'italic',
            fontSize: 16, letterSpacing: '0.01em',
            textDecoration: 'none',
            transition: 'border-color 360ms ease, color 360ms ease',
          }}
        >
          Book Leavers Night <span style={{ color: GOLD }}>✦</span>
        </Link>
      </footer>
    </motion.article>
  </section>
);

// =========================================================================
// SECTION 4.5 — WHAT REMAINS ✦ (iter207)
// The final emotional movement: participation flowing into preservation.
// The page no longer ends at "the room reacting together" — it resolves
// into "what the year group returns to afterwards."
//
// Constitutional locks from the iter207 brief:
//   • This is NOT a product showcase, NOT a feature launch, NOT a card grid.
//   • Three preservation artefacts as quiet editorial rows.
//   • Hierarchy emerges from pacing + typography, not chrome.
//   • The Documentary lands as the deepest artefact via spacing, not weight.
//   • Visual feeling: "the room cooling down after the evening ended."
//
// Position: between LeaversNightPackage and Closing — between the package
// (what you book) and the closing line (what the year group becomes). This
// is the emotional aftermath.
// =========================================================================
const PRESERVATION_LAYER = [
  {
    title: 'The Final Night ✦',
    line:  'A printed chronicle built from the room itself.',
    testId: 'preservation-final-night',
  },
  {
    title: 'Silent Film ✦',
    line:  'A black & white fragment of the evening, remembered like cinema.',
    testId: 'preservation-silent-film',
  },
  {
    title: 'The Documentary ✦',
    line:  'The year group telling its own story back to itself.',
    testId: 'preservation-documentary',
  },
];

const WhatRemains = () => (
  <section data-testid="schools-what-remains" style={{
    background: WALNUT_DEEP,
    // Slightly more vertical breathing than its neighbours — this is the
    // emotional decompression moment, not another section to scroll past.
    padding: 'clamp(112px, 16vh, 160px) 24px',
  }}>
    <motion.header
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-12%' }}
      transition={{ duration: 1.2, ease: [0.22, 0.61, 0.36, 1] }}
      style={{ maxWidth: 720, margin: '0 auto', textAlign: 'center' }}
    >
      <p style={{
        margin: 0, marginBottom: 28,
        fontFamily: SERIF, fontStyle: 'italic',
        fontSize: 11, letterSpacing: '0.28em', textTransform: 'uppercase',
        color: GOLD_MUTED,
      }} data-testid="what-remains-eyebrow">
        What remains <span style={{ color: GOLD, fontStyle: 'normal' }}>✦</span>
      </p>
      <h2 style={{
        margin: 0,
        fontFamily: SERIF, fontWeight: 500,
        fontSize: 'clamp(28px, 3.6vw, 38px)', lineHeight: 1.18,
        letterSpacing: '-0.01em', color: IVORY,
      }} data-testid="what-remains-headline">
        Some nights end.
        <br />
        <span style={{ fontStyle: 'italic' }}>Some keep speaking afterwards.</span>
      </h2>
      <p style={{
        margin: '24px auto 0', maxWidth: 520,
        fontFamily: SERIF, fontStyle: 'italic',
        fontSize: 'clamp(15px, 1.7vw, 17px)', lineHeight: 1.65,
        color: IVORY_DIM,
      }} data-testid="what-remains-subline">
        Long after the hall empties, the year group still has somewhere to return to.
      </p>
    </motion.header>

    {/* Three preservation artefacts. Quiet rows — no cards, no borders,
        no icons, no CTAs. Hierarchy is rhythm: each row holds the same
        typography, but the Documentary lands last so the page exhales
        on the deepest artefact. */}
    <div style={{
      maxWidth: 560, margin: '72px auto 0',
      display: 'flex', flexDirection: 'column',
      gap: 'clamp(48px, 6vh, 72px)',
    }} data-testid="what-remains-layer">
      {PRESERVATION_LAYER.map((item, i) => (
        <motion.div
          key={item.testId}
          data-testid={item.testId}
          initial={{ opacity: 0, y: 8 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-10%' }}
          transition={{
            duration: 1.1, ease: [0.22, 0.61, 0.36, 1],
            delay: 0.08 * i,
          }}
          style={{ textAlign: 'center' }}
        >
          <p style={{
            margin: 0, marginBottom: 14,
            fontFamily: SERIF, fontWeight: 500,
            fontSize: 'clamp(22px, 2.6vw, 26px)', lineHeight: 1.3,
            letterSpacing: '-0.005em', color: IVORY,
          }}>
            {item.title}
          </p>
          <p style={{
            margin: 0,
            fontFamily: SERIF, fontStyle: 'italic',
            fontSize: 'clamp(15px, 1.7vw, 17px)', lineHeight: 1.6,
            color: IVORY_DIM,
            maxWidth: 460, marginLeft: 'auto', marginRight: 'auto',
          }}>
            {item.line}
          </p>
        </motion.div>
      ))}
    </div>
  </section>
);

// =========================================================================
// SECTION 5 — QUIET CLOSING
// =========================================================================
const Closing = () => (
  <section data-testid="schools-closing" style={{
    background: WALNUT,
    padding: 'clamp(96px, 14vh, 144px) 24px',
  }}>
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-15%' }}
      transition={{ duration: 1.3, ease: [0.22, 0.61, 0.36, 1] }}
      style={{ maxWidth: 560, margin: '0 auto', textAlign: 'center' }}
    >
      <p style={{
        margin: 0,
        fontFamily: SERIF, fontStyle: 'italic',
        fontSize: 'clamp(20px, 2.6vw, 26px)', lineHeight: 1.5,
        color: IVORY,
      }} data-testid="schools-closing-copy">
        A year group, writing itself <span style={{ color: GOLD, fontStyle: 'normal' }}>✦</span>
      </p>
    </motion.div>
  </section>
);

// =========================================================================
// GLOBAL STYLES
// =========================================================================
const GlobalStyles = () => (
  <style>{`
    [data-testid="schools-page"] ::selection {
      background: rgba(212,170,82,0.22);
      color: #FFF5DA;
    }
    @media (prefers-reduced-motion: reduce) {
      [data-testid="schools-page"] * {
        animation-duration: 0.001ms !important;
        animation-iteration-count: 1 !important;
        transition-duration: 0.001ms !important;
      }
    }
  `}</style>
);
